import { getBrand } from '../app/constants/brands';
import { clearProductionChoice } from './brandEnvChoice';
import { usesOwnOrigin } from './ownOriginHosts';

export type RuntimeTarget = 'development' | 'staging' | 'production';

export const RUNTIME_TARGET_STORAGE_KEY = 'autopilotee_runtime_target';

const STORAGE_KEY = RUNTIME_TARGET_STORAGE_KEY;

/**
 * Staging web hosts, one per brand. All serve the same staging build.
 * staging.handysurrogate.com serves the Lenvoy brand until staging.lenvoy.co exists.
 */
const STAGING_WEB_HOSTS = new Set([
  'staging.autopilotee.com',
  'staging.lenvoy.co',
  'staging.handysurrogate.com',
]);
const PRODUCTION_WEB_HOSTS = new Set(['autopilotee.com', 'www.autopilotee.com']);

export function getBuildDefaultTarget(): RuntimeTarget {
  const apiEnv = (process.env.REACT_APP_API_ENV || 'development').toLowerCase();
  if (apiEnv === 'staging') return 'staging';
  if (apiEnv === 'production') return 'production';
  return 'development';
}

export function getHostDefaultTarget(): RuntimeTarget {
  const host = window.location.hostname.toLowerCase();
  if (STAGING_WEB_HOSTS.has(host)) return 'staging';
  if (PRODUCTION_WEB_HOSTS.has(host)) return 'production';
  return 'development';
}

/** Effective target for this page load (host wins on staging/production domains). */
export function getCurrentRuntimeTarget(): RuntimeTarget {
  const hostDefault = getHostDefaultTarget();
  if (hostDefault === 'staging' || hostDefault === 'production') {
    return hostDefault;
  }

  const buildDefault = getBuildDefaultTarget();

  // yarn start:staging / build:staging on localhost — always use the staging build target.
  if (buildDefault !== 'development') {
    return buildDefault;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY) as RuntimeTarget | null;
    if (stored === 'production') {
      return stored;
    }
    // Profile → Staging redirects off localhost; stale "staging" breaks Firebase on dev builds.
    if (stored === 'staging') {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore
  }

  return 'development';
}

export function isStagingTarget(): boolean {
  return getCurrentRuntimeTarget() === 'staging';
}

export function getAvailableSwitchTargets(): RuntimeTarget[] {
  const buildDefault = getBuildDefaultTarget();
  const onStagingHost = getHostDefaultTarget() === 'staging';
  const onProductionHost = getHostDefaultTarget() === 'production';

  if (onStagingHost) {
    return ['staging', 'production'];
  }
  if (onProductionHost || buildDefault === 'production') {
    return ['production', 'staging'];
  }
  return ['development', 'staging'];
}

export function getStagingWebUrl(): string {
  return process.env.REACT_APP_WEB_URL_STAGING || 'https://staging.autopilotee.com';
}

export function getProductionWebUrl(): string {
  return process.env.REACT_APP_WEB_URL_PROD || 'https://autopilotee.com';
}

/**
 * Spec 027: where the Environment switch goes for the brand being viewed. Before this every brand
 * went to Autopilotee's sites, so lenvoy.co's "Staging" opened staging.autopilotee.com. Autopilotee
 * (and a brand without a testing site) keeps the configured URLs.
 */
export function brandStagingWebUrl(brand = getBrand()): string {
  if (brand.id === 'autopilotee' || !brand.stagingWebHost) return getStagingWebUrl();
  return `https://${brand.stagingWebHost}`;
}

/** The brand's production site; `?env=production` tells its redirect to leave this visitor there. */
export function brandProductionWebUrl(brand = getBrand()): string {
  const base = brand.id === 'autopilotee' ? getProductionWebUrl() : `https://${brand.brandSite}`;
  return `${base.replace(/\/$/, '')}/?env=production`;
}

export function getGraphqlBaseUrl(): string {
  const target = getCurrentRuntimeTarget();
  if (target === 'staging') {
    return process.env.REACT_APP_API_URL_STAGING || 'https://staging-api.autopilotee.com/';
  }
  if (target === 'production') {
    const isDealershipSite =
      window.location.hostname === 'autopiloteecars.com' ||
      window.location.hostname === 'www.autopiloteecars.com' ||
      window.location.hostname === 'jidosojuholding.com' ||
      window.location.hostname === 'www.jidosojuholding.com' ||
      window.location.hostname === 'instasharings.com' ||
      window.location.hostname === 'www.instasharings.com';
    if (isDealershipSite || usesOwnOrigin()) return '/';
    return process.env.REACT_APP_API_URL_PROD || 'https://autopilotee.com/';
  }
  return process.env.REACT_APP_API_URL_DEV || 'http://localhost:8000/';
}

export const STAGING_LOGIN_PROMPT = {
  title: 'Test environment',
  body: "You are signing in to Autopilotee's staging environment. Bookings, payments, and messages here are for testing only and are not real. Test cards only; no real charges.",
  continueLabel: 'Continue to staging',
  cancelLabel: 'Cancel',
};

export const STAGING_OAUTH_SIGN_IN_PROMPT = {
  title: 'Testing mode',
  body: 'Please use email/password sign in rather than Google or Apple sign in in testing mode. Testing mode only supports email/password sign in.',
};

/**
 * Google sign-in is not offered on staging (testing mode): the sign-in and
 * sign-up modals hide the button, its "or" divider and its terms line. The
 * Flutter app hides it too (`SocialSignInOptions`).
 */
export function showGoogleSignIn(): boolean {
  return !isStagingTarget();
}

/** Returns true when OAuth redirect was blocked (staging). */
export function promptStagingOAuthBlocked(): boolean {
  if (!isStagingTarget()) {
    return false;
  }
  window.alert(`${STAGING_OAUTH_SIGN_IN_PROMPT.title}\n\n${STAGING_OAUTH_SIGN_IN_PROMPT.body}`);
  return true;
}

export async function switchRuntimeTarget(next: RuntimeTarget): Promise<void> {
  const allowed = getAvailableSwitchTargets();
  if (!allowed.includes(next)) {
    throw new Error(`Cannot switch to ${next} from this build/host`);
  }

  if (next === 'staging') {
    const confirmed = window.confirm(
      `${STAGING_LOGIN_PROMPT.title}\n\n${STAGING_LOGIN_PROMPT.body}`,
    );
    if (!confirmed) return;
  }

  const [{ authActions }, { client }, { default: firebaseService }] = await Promise.all([
    import('../store/authStore'),
    import('../app/services/apollo'),
    import('../app/services/firebase/FirebaseService'),
  ]);
  await authActions.logout();

  try {
    await Promise.allSettled([client.clearStore(), firebaseService.signOut()]);
  } catch {
    // ignore
  }

  try {
    localStorage.removeItem('oidc.user');
    sessionStorage.clear();
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('oidc.') || key.includes('cognito')) {
        localStorage.removeItem(key);
      }
    });
    sessionStorage.removeItem('fcm_initialized');
    sessionStorage.removeItem('fcm_token_timestamp');
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // ignore
  }

  if (next === 'staging') {
    // On the brand's production site, picking Testing hands the choice back to the brand default.
    clearProductionChoice();
    window.location.href = brandStagingWebUrl();
    return;
  }
  if (next === 'production') {
    window.location.href = brandProductionWebUrl();
    return;
  }
  window.location.href = 'http://localhost:3000';
}
